import type { ApolloClient } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { Input } from 'antd'
import { useState } from 'react'
import {
  UpdateExternalIdDocument,
  type UpdateExternalIdMutation,
} from '~app/graph/generated/graphql'
import i18n from '~app/i18n/i18n'
import { useUpdatingContext } from '../useUpdatingContext'

/*
 * Hooks
 */

const useExternalId = (
  sourceId: string
): {
  loading: boolean
  updateExternalId: (
    newExternalId: string
  ) => Promise<ApolloClient.MutateResult<UpdateExternalIdMutation>>
} => {
  const { setUpdating } = useUpdatingContext()
  const [updateScanCenterMutation, { loading }] = useMutation(
    UpdateExternalIdDocument
  )
  return {
    loading,
    updateExternalId: newExternalId => {
      setUpdating(sourceId, true)
      return updateScanCenterMutation({
        variables: { id: sourceId, externalId: newExternalId },
      }).finally(() => setUpdating(sourceId, false))
    },
  }
}

/*
 * Component
 */
interface Props {
  sourceId: string
  externalId: string | null
}

const ExternalId = ({ sourceId, externalId }: Props) => {
  const [editing, setEditing] = useState(false)
  const { updateExternalId, loading } = useExternalId(sourceId)

  return editing ? (
    <div>
      <Input
        disabled={loading}
        autoFocus
        defaultValue={externalId || ''}
        onBlur={() => setEditing(false)}
        onKeyDown={e => e.keyCode === 27 && setEditing(false)}
        onPressEnter={e => {
          updateExternalId(e.currentTarget.value)
          setEditing(false)
        }}
      />
    </div>
  ) : (
    <div
      role="button"
      className="editable-cell-value-wrap"
      onClick={() => setEditing(true)}
    >
      {externalId || i18n.t('misc.noExternalId')}
    </div>
  )
}

export default ExternalId
